import {Routes, Route} from 'react-router-dom'
import { AuthProvider } from '@contexts/authContext';
import { ProtectedAuthRoute} from '@components/General/routes/ProtectedAuthRoute';
import { NoAuthRoute} from '@components/General/routes/NoAuthRoute';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PopupProvider } from '@contexts/popupContext';
import HTTPErrorListener from '@components/General/HTTPErrorListener';
import { ToastProvider } from '@shared/designSystem/messages/ToastProvider';

import Dashboard from './Dashboard';
import UnauthorizedLanding from '@screens/LandingUnauthorized';
import LogInScreen from '@screens/LogIn';
import { isMobileOnly } from 'react-device-detect';
import MobileDashboard from '@screens/mobile/MobileDashboard';
import { Application } from "./core/Application"
import { Logger } from '@shared/utils/SharedLogger';
import { firebaseAuth } from '@contexts/firebase';
import { getIdToken } from 'firebase/auth'
import { SharedApplication } from '@shared/core/SharedApplication';
import { HTTPClient } from '@shared/core/HTTPClient';
import { CoreDependencies as SharedCoreDependencies } from '@shared/core/CoreDependencies';
import { CoreDependencies } from '@core/CoreDependencies';


let refreshTokenPromise: Promise<string | null> | null = null
const refreshAuthToken = async (): Promise<string | null> => {
    Logger.log("refresh","Starting token refresh")
    const currentUser = firebaseAuth.currentUser
    if (!currentUser) {
        return null
    }

    if (!refreshTokenPromise) {
        refreshTokenPromise = getIdToken(currentUser, true)
            .then((token) => {
                Logger.log("refresh","Token refreshed successfully")
                Application.shared().setToken(token)
                SharedApplication.shared().setToken(token)
                return token
            })
            .catch((error) => {
                Logger.logError("Auth token refresh failed", error?.message ?? "Unknown error")
                return null
            })
            .finally(() => {
                refreshTokenPromise = null
            })
    }

    return refreshTokenPromise
}
const authenticatedHTTPClient = new HTTPClient()
authenticatedHTTPClient.retryCallback = async (request, status, attemptNumber: number):Promise<boolean> => {
    Logger.log("refresh callback 1", "called with "+status + " attempt: "+attemptNumber)
    if (status !== 401) {
        return false
    }

    if (attemptNumber > 0) {
        return false
    }

    const newToken = await refreshAuthToken()
    if (!newToken) {
        Application.shared().handleSessionExpired()
        return false
    }

    return true
}

let coreDependencies = new CoreDependencies(
    authenticatedHTTPClient,
)

let sharedCoreDependencies = new SharedCoreDependencies(
    authenticatedHTTPClient,
    new HTTPClient(),
)

Application.setDependencies(
    coreDependencies,
    sharedCoreDependencies
)

SharedApplication.setDependencies(
    sharedCoreDependencies
)

const theme = createTheme({
  typography: {
    fontFamily: 'Inter',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Light.ttf) format('truetype');
          font-style: normal;
          font-weight: 300;
        }
        
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Regular.ttf) format('truetype');
          font-style: normal;
          font-weight: 400;
        }
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Bold.ttf) format('truetype');
          font-style: normal;
          font-weight: 700;
        }
        
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-ExtraBold.ttf) format('truetype');
          font-style: normal;
          font-weight: 800;
        }
        
        @font-face {
          font-family: 'Readex Pro';
          src: url(../assets/fonts/readexpro/ReadexPro-Medium.ttf) format('truetype');
          font-style: normal;
          font-weight: 500;
        }
      `,
    },
  },
});


const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <PopupProvider>
          <ToastProvider>
            <HTTPErrorListener />
            <Routes>
              <Route path='/login' element=
              {
                <NoAuthRoute>
                  <LogInScreen/>
                </NoAuthRoute>
              } /> 
              <Route path='/unauthorized' element=
              {
                <NoAuthRoute>
                  <UnauthorizedLanding/>
                </NoAuthRoute>
              } /> 
              <Route path='*' element=
              {
                <ProtectedAuthRoute>
                  {isMobileOnly ? <MobileDashboard /> : <Dashboard />}
                </ProtectedAuthRoute>
              } /> 
            </Routes>
          </ToastProvider>
        </PopupProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App;
